import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import { LoadingSpinner } from '../core/LoadingSpinner';
import {
  useActivateApp,
  useDeactivateApp,
  useGetAppArray,
} from '../hooks/reactQuerys/AppQueries';
import { useAlert } from '../context/AlertContext';
import { IApp } from '../Interfaces/IApp';
import { ExtensionConfigDialog } from './ExtensionConfigDialog';

const isCore = (a: IApp) => a.IsCore === true;
const isEnabled = (a: IApp) => a.IsLocallyEnabled === true;
const isActive = (a: IApp) => isCore(a) || isEnabled(a);

const groupRank = (a: IApp): number => {
  if (a.IsLicensed === false) return 2;
  return isActive(a) ? 0 : 1;
};

const sortApps = (apps: IApp[]) =>
  [...apps].sort((a, b) => {
    const ra = groupRank(a);
    const rb = groupRank(b);
    if (ra !== rb) return ra - rb;
    if (isCore(a) !== isCore(b)) return isCore(a) ? -1 : 1;
    return (a.App || '').localeCompare(b.App || '');
  });

interface ICardProps {
  app: IApp;
  onToggle: (app: IApp, next: boolean) => Promise<void>;
  onConfigure: (app: IApp) => void;
  /** Irgendwo läuft gerade ein Aktivieren/Deaktivieren — alle Switches
   *  blockieren, damit kein paralleler Toggle entsteht. */
  isMutating: boolean;
  /** Genau diese Card ist gerade in der Mutation — wir zeigen einen
   *  CircularProgress über dem Switch + dimmen die Card leicht. */
  isToggling: boolean;
}

const ExtensionCard: React.FC<ICardProps> = ({
  app,
  onToggle,
  onConfigure,
  isMutating,
  isToggling,
}) => {
  const theme = useTheme();
  const enabled = isEnabled(app);
  const core = isCore(app);
  const licensed = app.IsLicensed !== false;
  const effective = app.IsEffective ?? (core || enabled);
  const activated = app.idApp != null;

  const toggleDisabled = core || isMutating;

  return (
    <Card
      variant='outlined'
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: effective
          ? theme.palette.success.light
          : theme.palette.divider,
        position: 'relative',
        // Wenn die Mutation gerade fuer DIESE Card laeuft, dimmen wir die
        // ganze Card leicht ein — visuelles Signal, dass hier etwas
        // passiert. CircularProgress steckt zusaetzlich im Switch-Slot.
        opacity: isToggling ? 0.6 : 1,
        pointerEvents: isToggling ? 'none' : undefined,
        transition: 'opacity 150ms ease',
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            {app.IconUrl ? (
              <img
                src={app.IconUrl}
                alt={app.App}
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <LocalGroceryStoreIcon color='disabled' />
            )}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant='h6' noWrap>
              {app.App}
            </Typography>
            <Typography variant='caption' color='text.secondary' noWrap>
              {app.system_key}
            </Typography>
          </Box>
        </Stack>

        <Stack direction='row' spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          {core && <Chip size='small' color='primary' label='Kern' />}
          <Chip
            size='small'
            color={effective ? 'success' : 'default'}
            variant={effective ? 'filled' : 'outlined'}
            label={effective ? 'Aktiv' : 'Deaktiviert'}
          />
        </Stack>

        {app.Description && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            {app.Description}
          </Typography>
        )}
      </CardContent>

      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Tooltip
          title={
            core
              ? 'Systemrelevanter Bestandteil — kann nicht deaktiviert werden.'
              : isToggling
                ? 'Status wird gerade aktualisiert …'
                : enabled
                  ? 'Aktiv. Klicken zum Deaktivieren.'
                  : 'Deaktiviert. Klicken zum Aktivieren.'
          }
        >
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <Switch
              checked={core || enabled}
              disabled={toggleDisabled}
              onChange={(_, next) => onToggle(app, next)}
            />
            {isToggling && (
              <CircularProgress
                size={20}
                sx={{
                  position: 'absolute',
                  // Spinner mittig über dem Switch-Thumb platzieren
                  left: '50%',
                  top: '50%',
                  marginLeft: '-10px',
                  marginTop: '-10px',
                  // Über dem (jetzt halb-transparenten) Switch-Thumb sichtbar
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        </Tooltip>
        {licensed ? (
          app.HasSettings ? (
            <Button
              size='small'
              variant='outlined'
              disabled={!activated}
              onClick={() => onConfigure(app)}
            >
              Konfigurieren
            </Button>
          ) : (
            <Box />
          )
        ) : (
          <Button
            size='small'
            variant='contained'
            color='primary'
            component='a'
            href={`mailto:team@lextorbyte.de?subject=${encodeURIComponent(
              `Erweiterung anfragen: ${app.App}`
            )}&body=${encodeURIComponent(
              `Sehr geehrtes LexTorByte-Team,\n\nich interessiere mich fuer die Erweiterung "${app.App}" (${app.system_key}) und bitte um ein Angebot bzw. weitere Informationen.\n\nMit freundlichen Gruessen`
            )}`}
          >
            Anfragen
          </Button>
        )}
      </Box>
    </Card>
  );
};

export const ExtensionsView: React.FC = () => {
  const { appArray, isLoadingAppArray, refetchAppArray } = useGetAppArray();
  const { activateApp, isPendingActivateApp } = useActivateApp();
  const { deactivateApp, isPendingDeactivateApp } = useDeactivateApp();
  const { showAlert } = useAlert();
  const [configApp, setConfigApp] = useState<IApp | null>(null);
  // Welche Erweiterung gerade getoggelt wird — die Card zeigt dann eine
  // CircularProgress über dem Switch + eine leichte Dimmung, sodass der
  // Notar sofort sieht: „etwas läuft, gleich".
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const sorted = useMemo(
    () => (appArray ? sortApps(appArray) : []),
    [appArray]
  );

  const counts = useMemo(() => {
    const total = sorted.length;
    const active = sorted.filter(isActive).length;
    return { total, active };
  }, [sorted]);

  if (isLoadingAppArray) return <LoadingSpinner />;

  if (!appArray) {
    return (
      <Alert
        severity='error'
        action={
          <Button
            color='inherit'
            size='small'
            onClick={() => refetchAppArray()}
          >
            Erneut versuchen
          </Button>
        }
      >
        Erweiterungen konnten nicht geladen werden.
      </Alert>
    );
  }

  const handleToggle = async (app: IApp, next: boolean) => {
    setTogglingKey(app.system_key);
    try {
      if (next) {
        await activateApp(app.system_key);
      } else {
        await deactivateApp(app.system_key);
      }
      await refetchAppArray();
      showAlert({
        text: next
          ? `${app.App} wurde aktiviert.`
          : `${app.App} wurde deaktiviert.`,
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Statusänderung fehlgeschlagen.',
        severity: 'error',
      });
    } finally {
      setTogglingKey(null);
    }
  };

  const isMutating = isPendingActivateApp || isPendingDeactivateApp;

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='baseline'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Typography variant='h4'>Erweiterungen</Typography>
        <Typography variant='body2' color='text.secondary'>
          {counts.active} aktiv · {counts.total} verfügbar
        </Typography>
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Es werden nur Erweiterungen angezeigt, die für Ihre Lizenz freigegeben
        sind. Eine Erweiterung ist erst dann aktiv, wenn sie zusätzlich lokal
        eingeschaltet wird. Systemrelevante, für den Betrieb von DiNo
        erforderliche Bestandteile sind dauerhaft aktiv und können nicht
        deaktiviert werden.
      </Typography>

      {sorted.length === 0 ? (
        <Alert severity='info'>
          Aktuell sind keine Erweiterungen für Ihre Lizenz freigegeben.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sorted.map(app => (
            <Grid item xs={12} sm={6} md={4} key={app.system_key}>
              <ExtensionCard
                app={app}
                onToggle={handleToggle}
                onConfigure={setConfigApp}
                isMutating={isMutating}
                isToggling={togglingKey === app.system_key}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {configApp && configApp.idApp != null && (
        <ExtensionConfigDialog
          app={{ ...configApp, idApp: configApp.idApp }}
          open
          onClose={() => setConfigApp(null)}
        />
      )}
    </Box>
  );
};
